import { Component, AfterContentChecked, OnInit, OnDestroy } from '@angular/core';
import { NotificationService } from '../shared/header/notification.service';
import { AppSettings } from '../app.settings';
import { AppState } from '../app.service';
import { SpriteMap } from '../shared-components/sprite.map';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

interface Banner {
    position: number;
    name: string;
    localizedOnly?: boolean;
    englishOutsideLocalized?: boolean;
}

@Component({
    selector: 'home',
    templateUrl: 'home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements AfterContentChecked, OnInit, OnDestroy {

    public static readonly LOCALIZED_BANNER_LANGUAGES: ReadonlyArray<string> = ['en', 'fr'];

    public languageFlag: string;
    public storeUrl: string = `${AppSettings.PUBLICATIONS_STORE}`;

    public components: Array<any>;
    public componentsMap: SpriteMap = {
        format: 'GRID',
        numberOfHorizontalImages: 2,
        numberOfVerticalImages: 4
    };

    public banners: Banner[];
    public bannersMap: SpriteMap = {
        format: 'GRID',
        numberOfHorizontalImages: 2,
        numberOfVerticalImages: 3
    }

    public subscription: Subscription;
    private translationSub: Subscription = new Subscription();

    constructor(private readonly notificationService: NotificationService,
        private readonly appService: AppState,
        private readonly router: Router,
        private readonly translate: TranslateService
    ) {
        this.components = [
            {link: '/esn-entry', href: null, nameKey: 'Engine Parts', name: 'Engine Parts', position: 0},
            {link: '/gsn-entry', href: null, nameKey: 'Power Generation', name: 'Power Generation', position: 2},
            {link: '/ctt-entry', href: null, nameKey: 'Turbochargers', name: 'Turbochargers', position: 3},
            {link: '/esn-entry/npbu', href: null, nameKey: 'Electrified Power Products', name: 'Electrified Power Products', position: 7},
            {link: '/atsn-entry', href: null, nameKey: 'Aftertreatment', name: 'Aftertreatment', position: 4},
            {link: null, href: this.storeUrl, nameKey: 'Publications', name: 'Publications', position: 5},
            {link: '/mst-entry/main/home-page', href: null, nameKey: 'Service Tools and Consumables', name: 'Service Tools and Consumables', position: 6},
        ]

        // englishOutsideLocalized: uses the en sprite for languages outside LOCALIZED_BANNER_LANGUAGES.
        this.banners = [
            { position: 0, name: 'Banner 1' },
            { position: 1, name: 'Banner 2' },
            { position: 2, name: 'Banner 3' },
            { position: 3, name: 'Banner 4' },
            { position: 4, name: 'Banner 5', localizedOnly: true },
            { position: 5, name: 'Banner 6', englishOutsideLocalized: true },
        ]
    }

    get visibleBanners(): Banner[] {
        const localized = this.hasLocalizedBanners();
        return this.banners.filter(b => localized || !b.localizedOnly);
    }

    public ngOnInit(): void {
        this.translationSub.add(
            this.translate.stream([
                'Engine Parts',
                'Power Generation',
                'Turbochargers',
                'Electrified Power Products',
                'Aftertreatment',
                'Publications',
                'Service Tools and Consumables'
            ]).subscribe({
                next: (translations) => {
                    this.components.forEach(component => {
                        if (component.nameKey && translations[component.nameKey]) {
                            component.name = translations[component.nameKey];
                        }
                    });
                },
                error: (err) => console.error('Error fetching translations:', err)
            })
        );
    }

    public navigate(component: any, target?: string) {
        if (component.href) window.open(component.href, target);
        else if (component.link) this.router.navigate([component.link]);
    }

    public getAssetsLink(path: string, bannerPosition: number = 0): string {
        return bannerPosition >= 0 ? this.appService.getAssetsLink(path) : 'NA';
    }

    public ngAfterContentChecked() {
        if (!this.subscription) {
            this.subscription = this.notificationService.notificationTranslateView$.subscribe({
                next: (message) => this.languageFlag = message || 'en',
                error: (err) => console.error('Error receiving language change notification:', err)
            });
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription) {this.subscription.unsubscribe();}
        if (this.translationSub) {this.translationSub.unsubscribe();}
    }

    public getEffectiveLanguage(banner: Banner): string {
        if (banner.englishOutsideLocalized && !this.hasLocalizedBanners()) return 'en';
        return this.languageFlag === 'pt-BR' ? 'pt' : (this.languageFlag ?? 'en');
    }

    private hasLocalizedBanners(): boolean {
        return HomeComponent.LOCALIZED_BANNER_LANGUAGES.includes(this.languageFlag ?? 'en');
    }
}
